'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import type { NearbyRestaurantRecord } from '@bip/api-client';
import { IMG } from '../../../imgPrefix';
import './NearbyRestaurantCard.css';

const FALLBACK_IMAGE = `${IMG}/image/no-image.png`;

// resOS often returns a relative CDN path rather than an absolute URL.
// Mirrors `resolveRestaurantImage` in app/restaurants/config.ts — duplicated
// (not imported) because this card is shared across domains and must not
// pull restaurants-domain code into e.g. the Sports bundle.
const RESOS_CDN_BASE = process.env.NEXT_PUBLIC_RESOS_CDN_BASE || 'https://resos.imgix.net';

function resolveRestaurantImage(url?: string | null): string {
  if (!url) return FALLBACK_IMAGE;
  if (url.startsWith('http')) return url;
  return `${RESOS_CDN_BASE}${url.startsWith('/') ? '' : '/'}${url}`;
}

function RestaurantImage({ src, alt }: { src?: string | null; alt: string }) {
  const [currentSrc, setCurrentSrc] = useState(resolveRestaurantImage(src));

  useEffect(() => {
    setCurrentSrc(resolveRestaurantImage(src));
  }, [src]);

  return (
    <img
      src={currentSrc}
      alt={alt}
      loading="lazy"
      onError={() => {
        if (currentSrc !== FALLBACK_IMAGE) setCurrentSrc(FALLBACK_IMAGE);
      }}
      className="rnr-img"
    />
  );
}

/** Append `?date=<ISO>` to the restaurant's own `detailPageUrl` so
 *  the restaurant booking widget on the destination page prefills
 *  with the SAME date as the referring event (Sports/Concerts
 *  performance date, or the Restaurants own-domain nearby strip's
 *  own reservation date). Preserves any query params the backend
 *  already baked into `detailPageUrl`.
 *
 *  `bookingDate` is expected in ISO `YYYY-MM-DD` — every caller
 *  today ({sports,concerts}/[id]/page.tsx + booking/[id]/success +
 *  RestaurantDetailNearby) reformats to ISO before passing. Falsy
 *  → no date param appended. */
function bookHref(url: string | undefined | null, bookingDate?: string): string {
  const base = url || '/restaurants';
  if (!bookingDate) return base;
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(bookingDate) ? bookingDate : '';
  if (!iso) return base;
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}date=${iso}`;
}

export function NearbyRestaurantCard({
  restaurant,
  referenceName,
  bookingDate,
}: {
  restaurant: NearbyRestaurantRecord;
  referenceName?: string;
  /** ISO `YYYY-MM-DD`. Forwarded onto the "Book now" URL so the
   *  restaurant booking widget prefills the SAME date the referring
   *  page carried. */
  bookingDate?: string;
}) {
  const location = [restaurant.address?.city, restaurant.address?.country].filter(Boolean).join(', ');
  const dist = restaurant.distanceInMiles;

  return (
    <div className="rnr-card">
      <div className="rnr-img-wrap">
        <RestaurantImage src={restaurant.image} alt={restaurant.name} />
      </div>

      <div className="rnr-body">
        <div className="rnr-name">{restaurant.name}</div>

        {(location || dist != null) && (
          <div className="rnr-location">
            <i className="text-lg fal fa-map-marker-alt rnr-location__icon" />
            <span>
              {location}
              {dist != null && (
                <>, {dist.toFixed(1)} miles {referenceName ? ` from ${referenceName}` : ''}</>
              )}
            </span>
          </div>
        )}
      </div>

      <div className="rnr-book-wrap">
        <Link
          href={bookHref(restaurant.detailPageUrl, bookingDate)}
          className="rnr-book"
          target="_blank"
          rel="noopener noreferrer"
        >
          Book now
        </Link>
      </div>
    </div>
  );
}
